import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  CalendarClock,
  CheckCircle2,
  Info,
  Megaphone,
  OctagonAlert,
  Pause,
  Pencil,
  Play,
  Plus,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { useAppServices } from "../../hook/services";
import { cx, formatDate } from "../../utils";
import Button from "../../components/ui/Button";
import Input, { Label, Textarea } from "../../components/ui/Input";
import { useConfirm } from "../../components/ui/ConfirmDialog";
import { useToast } from "../../components/ui/Toast";
import TabLinks from "../../components/ui/TabLinks";
import Pagination from "../../components/ui/Pagination";
import ListFilters from "../../components/ui/ListFilters";
import useListQuery, { useSearchInput } from "../../hook/useListQuery";

const LIST_DEFAULTS = { page: 1, limit: 20, state: "", tone: "", appId: "", q: "" };
// Matches the server's state filters (backend routes/announcements/services.js).
const STATE_OPTIONS = [
  ["live", "Live"],
  ["scheduled", "Scheduled"],
  ["ended", "Ended"],
  ["paused", "Paused"],
];

/**
 * Announcements for the notification bar at the top of app pages.
 *
 * Write one, see exactly how it will look, choose which apps show it and when,
 * then publish. Live ones can be paused without deleting them.
 */

const TONES = {
  info: { label: "Info", icon: Info, bar: "bg-brand-50 text-brand-900 border-brand-200/70", iconWrap: "bg-brand-600 text-white", link: "bg-brand-600 text-white", muted: "text-brand-800/80", control: "text-brand-700", chip: "bg-brand-50 text-brand-700 ring-brand-200/70" },
  success: { label: "Success", icon: CheckCircle2, bar: "bg-emerald-50 text-emerald-900 border-emerald-200/70", iconWrap: "bg-emerald-600 text-white", link: "bg-emerald-600 text-white", muted: "text-emerald-800/80", control: "text-emerald-700", chip: "bg-emerald-50 text-emerald-700 ring-emerald-200/70" },
  warning: { label: "Warning", icon: AlertTriangle, bar: "bg-amber-50 text-amber-950 border-amber-200/80", iconWrap: "bg-amber-500 text-white", link: "bg-amber-600 text-white", muted: "text-amber-900/80", control: "text-amber-800", chip: "bg-amber-50 text-amber-700 ring-amber-200/80" },
  critical: { label: "Critical", icon: OctagonAlert, bar: "bg-rose-50 text-rose-950 border-rose-200/80", iconWrap: "bg-rose-600 text-white", link: "bg-rose-600 text-white", muted: "text-rose-900/80", control: "text-rose-700", chip: "bg-rose-50 text-rose-700 ring-rose-200/80" },
};

const BLANK = {
  title: "",
  message: "",
  tone: "info",
  linkLabel: "",
  linkUrl: "",
  appIds: [],
  startsAt: "",
  endsAt: "",
  active: true,
  dismissible: true,
};

// <input type="datetime-local"> wants local time without a zone.
const toLocalInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Where an announcement stands right now, as the list shows it.
const stateOf = (a) => {
  const now = Date.now();
  if (!a.active) return { label: "Paused", cls: "bg-ink-100 text-ink-600 ring-ink-200/80", dot: "bg-ink-400" };
  if (a.endsAt && new Date(a.endsAt).getTime() <= now) return { label: "Ended", cls: "bg-ink-100 text-ink-500 ring-ink-200/80", dot: "bg-ink-300" };
  if (a.startsAt && new Date(a.startsAt).getTime() > now) return { label: "Scheduled", cls: "bg-violet-50 text-violet-700 ring-violet-200/70", dot: "bg-violet-500" };
  return { label: "Live", cls: "bg-emerald-50 text-emerald-700 ring-emerald-200/80", dot: "bg-emerald-500", live: true };
};

// The bar exactly as app pages render it (see frontend/src/components/AnnouncementBar.jsx).
const BarPreview = ({ a }) => {
  const t = TONES[a.tone] || TONES.info;
  const Icon = t.icon;
  return (
    <div className={cx("rounded-xl border px-3 py-2.5 shadow-sm", t.bar)}>
      <div className="flex items-center gap-3">
        <span className={cx("grid h-7 w-7 shrink-0 place-items-center rounded-lg", t.iconWrap)}>
          <Icon size={15} />
        </span>
        <p className="min-w-0 flex-1 text-[13px] leading-snug">
          <span className="font-semibold">{a.title || "Your announcement title"}</span>
          {a.message && <span className={cx("ml-1.5", t.muted)}>{a.message}</span>}
        </p>
        {a.linkUrl && (
          <span className={cx("inline-flex h-7 items-center gap-1 rounded-lg px-2.5 text-[12px] font-semibold", t.link)}>
            {a.linkLabel || "Learn more"} <ArrowRight size={12} />
          </span>
        )}
        {a.dismissible && (
          <span className={cx("grid h-7 w-7 place-items-center", t.control)}>
            <X size={15} />
          </span>
        )}
      </div>
    </div>
  );
};

const Switch = ({ checked, onChange, label, hint }) => (
  <label className="flex cursor-pointer items-start justify-between gap-4">
    <span>
      <span className="block text-sm font-medium text-ink-800">{label}</span>
      {hint && <span className="mt-0.5 block text-xs text-ink-500">{hint}</span>}
    </span>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={cx(
        "relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
        checked ? "bg-brand-600" : "bg-ink-300",
      )}
    >
      <span className={cx("absolute left-0 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform", checked ? "translate-x-[18px]" : "translate-x-0.5")} />
    </button>
  </label>
);

const Announcements = () => {
  const { announcements, apps } = useAppServices();
  const toast = useToast();
  const confirm = useConfirm();

  // Paged and filtered on the server; the address holds the current view.
  const [query, setQuery] = useListQuery(LIST_DEFAULTS);
  const [search, setSearch] = useSearchInput(query.q, (q) => setQuery({ q }));
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1, limit: LIST_DEFAULTS.limit, counts: {} });
  const list = data.items;
  const [appList, setAppList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // "new" or an _id
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page: query.page, limit: query.limit });
    for (const key of ["state", "tone", "appId", "q"]) if (query[key]) params.set(key, query[key]);
    const { response, error } = await announcements.List(params.toString());
    if (error) toast(error?.message || error?.error || "Could not load announcements", "error");
    const next = response && Array.isArray(response.items) ? response : { items: [], total: 0, page: 1, pages: 1, limit: query.limit, counts: {} };
    if (next.items.length === 0 && next.total > 0 && query.page > next.pages) setQuery({ page: next.pages });
    setData(next);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.page, query.limit, query.state, query.tone, query.appId, query.q]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    apps.Get().then(({ response }) => setAppList(Array.isArray(response) ? response.filter((x) => x.appId) : []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = Boolean(query.q || query.state || query.tone || query.appId);
  const counts = data.counts || {};

  const appName = useMemo(() => Object.fromEntries(appList.map((x) => [x.appId, x.appName])), [appList]);

  const startNew = () => {
    setForm(BLANK);
    setEditingId("new");
  };

  const startEdit = (a) => {
    setForm({
      ...BLANK,
      ...a,
      message: a.message || "",
      linkLabel: a.linkLabel || "",
      linkUrl: a.linkUrl || "",
      appIds: a.appIds || [],
      startsAt: toLocalInput(a.startsAt),
      endsAt: toLocalInput(a.endsAt),
    });
    setEditingId(a._id);
  };

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async (e) => {
    e.preventDefault();
    if (form.title.trim().length < 3) {
      toast("Give the announcement a title of at least 3 characters.", "error");
      document.getElementById("an-title")?.focus();
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title,
      message: form.message,
      tone: form.tone,
      linkLabel: form.linkLabel,
      linkUrl: form.linkUrl,
      appIds: form.appIds,
      // Sent as ISO so the server reads the admin's local time correctly.
      startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : null,
      endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
      active: form.active,
      dismissible: form.dismissible,
    };
    const { error } =
      editingId === "new" ? await announcements.Create(payload) : await announcements.Update(editingId, payload);
    setSaving(false);
    if (error) {
      toast(error?.message || "Could not save the announcement", "error");
      return;
    }
    // Reloaded rather than patched: a new one belongs on page one, and an edit
    // can move it between states.
    if (editingId === "new" && query.page !== 1) setQuery({ page: 1 });
    else load();
    toast(editingId === "new" ? "Announcement published" : "Announcement updated");
    setEditingId(null);
  };

  const toggle = async (a) => {
    const { response, error } = await announcements.Update(a._id, { active: !a.active });
    if (error) {
      toast(error?.message || "Could not update the announcement", "error");
      return;
    }
    load();
    toast(response.active ? "Announcement resumed" : "Announcement paused");
  };

  const remove = async (a) => {
    const ok = await confirm({
      title: "Delete this announcement?",
      message: "It disappears from every app page straight away. This cannot be undone.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    const { error } = await announcements.Delete(a._id);
    if (error) {
      toast(error?.message || "Could not delete the announcement", "error");
      return;
    }
    load();
    if (editingId === a._id) setEditingId(null);
    toast("Announcement deleted");
  };

  const liveCount = counts.live || 0;
  const anyAtAll = STATE_OPTIONS.some(([id]) => counts[id] > 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-ink-900">Announcements</h1>
          <p className="mt-1 text-sm text-ink-500">
            Notices shown in a bar at the top of app pages — maintenance, new features, anything customers need to know.
            {liveCount > 0 && <span className="ml-1 font-medium text-emerald-700">{liveCount} live now.</span>}
          </p>
        </div>
        {!editingId && (
          <Button variant="gradient" onClick={startNew} icon={<Plus size={14} />}>
            New announcement
          </Button>
        )}
      </div>

      {editingId && (
        <form onSubmit={save} className="animate-fade-in overflow-hidden rounded-2xl border border-ink-200/70 bg-white shadow-card-elevated">
          <div className="flex items-center justify-between border-b border-ink-150 px-6 py-4">
            <h2 className="text-[15px] font-semibold text-ink-900">{editingId === "new" ? "New announcement" : "Edit announcement"}</h2>
            <button type="button" onClick={() => setEditingId(null)} className="grid h-8 w-8 place-items-center rounded-lg text-ink-400 hover:bg-ink-100 hover:text-ink-700" aria-label="Close">
              <X size={16} />
            </button>
          </div>

          {/* Live preview, exactly as customers will see it */}
          <div className="border-b border-ink-150 bg-ink-50/70 px-6 py-5">
            <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-wider text-ink-400">Preview</p>
            <BarPreview a={form} />
          </div>

          <div className="grid gap-8 px-6 py-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
            <div className="space-y-5">
              <div>
                <Label>Type</Label>
                <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4" role="radiogroup" aria-label="Type">
                  {Object.entries(TONES).map(([id, t]) => {
                    const Icon = t.icon;
                    const on = form.tone === id;
                    return (
                      <button
                        key={id}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        onClick={() => set("tone")(id)}
                        className={cx(
                          "flex items-center gap-2 rounded-xl border px-3 py-2.5 text-[13px] font-medium transition-all",
                          on ? cx("ring-1", t.chip, "border-transparent") : "border-ink-200 text-ink-600 hover:bg-ink-50",
                        )}
                      >
                        <span className={cx("grid h-6 w-6 place-items-center rounded-md", t.iconWrap)}>
                          <Icon size={13} />
                        </span>
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label htmlFor="an-title" required>Title</Label>
                <Input id="an-title" className="mt-1.5" maxLength={120} value={form.title} onChange={(e) => set("title")(e.target.value)} placeholder="e.g. Scheduled maintenance on Sunday" />
              </div>

              <div>
                <Label htmlFor="an-message" hint="optional">Message</Label>
                <Textarea id="an-message" className="mt-1.5" rows={3} maxLength={500} value={form.message} onChange={(e) => set("message")(e.target.value)} placeholder="Sending may pause for about 10 minutes from 2:00 AM UTC." />
                <p className="mt-1 text-right text-[11px] tabular-nums text-ink-400">{form.message.length}/500</p>
              </div>

              <div className="grid gap-4 sm:grid-cols-[12rem_minmax(0,1fr)]">
                <div>
                  <Label htmlFor="an-link-label" hint="optional">Button text</Label>
                  <Input id="an-link-label" className="mt-1.5" maxLength={40} value={form.linkLabel} onChange={(e) => set("linkLabel")(e.target.value)} placeholder="Learn more" />
                </div>
                <div>
                  <Label htmlFor="an-link-url" hint="optional">Button link</Label>
                  <Input id="an-link-url" className="mt-1.5" maxLength={500} value={form.linkUrl} onChange={(e) => set("linkUrl")(e.target.value)} placeholder="https://… or #support" />
                  <TabLinks value={form.linkUrl} label={form.linkLabel} onPick={(v) => setForm((f) => ({ ...f, ...v }))} />
                </div>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <Label>Show on</Label>
                <div className="mt-2 max-h-48 space-y-1 overflow-y-auto rounded-xl border border-ink-200 p-1.5">
                  <label className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] hover:bg-ink-50">
                    <input type="checkbox" className="h-4 w-4 rounded border-ink-300 text-brand-600" checked={form.appIds.length === 0} onChange={() => set("appIds")([])} />
                    <span className="font-medium text-ink-800">Every app</span>
                  </label>
                  {appList.map((app) => (
                    <label key={app.appId} className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] hover:bg-ink-50">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-ink-300 text-brand-600"
                        checked={form.appIds.includes(app.appId)}
                        onChange={(e) =>
                          set("appIds")(e.target.checked ? [...form.appIds, app.appId] : form.appIds.filter((x) => x !== app.appId))
                        }
                      />
                      <span className="truncate text-ink-700">{app.appName}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="an-start" hint="optional">Starts</Label>
                  <Input id="an-start" type="datetime-local" className="mt-1.5 px-2 text-[12.5px]" value={form.startsAt} onChange={(e) => set("startsAt")(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="an-end" hint="optional">Ends</Label>
                  <Input id="an-end" type="datetime-local" className="mt-1.5 px-2 text-[12.5px]" value={form.endsAt} onChange={(e) => set("endsAt")(e.target.value)} />
                </div>
              </div>

              <div className="space-y-4 rounded-xl border border-ink-200 p-4">
                <Switch checked={form.active} onChange={set("active")} label="Published" hint="Turn off to keep it as a draft, or pause it." />
                <Switch checked={form.dismissible} onChange={set("dismissible")} label="Customers can dismiss it" hint="Turn off for notices they must see." />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-ink-150 bg-ink-50/50 px-6 py-3.5">
            <Button type="button" variant="ghost" onClick={() => setEditingId(null)}>
              Cancel
            </Button>
            <Button type="submit" variant="gradient" loading={saving} icon={!saving && <Save size={14} />}>
              {editingId === "new" ? (form.active ? "Publish" : "Save draft") : "Save changes"}
            </Button>
          </div>
        </form>
      )}

      <div className="overflow-hidden rounded-2xl border border-ink-200/70 bg-white shadow-card">
        {(anyAtAll || filtered) && (
          <ListFilters
            search={search}
            onSearch={setSearch}
            placeholder="Search title or message"
            filters={[
              {
                label: "State",
                value: query.state,
                onChange: (state) => setQuery({ state }),
                options: [["", "Any state"], ...STATE_OPTIONS.map(([id, label]) => [id, `${label} (${counts[id] || 0})`])],
                className: "w-40",
              },
              {
                label: "Type",
                value: query.tone,
                onChange: (tone) => setQuery({ tone }),
                options: [["", "All types"], ...Object.entries(TONES).map(([id, t]) => [id, t.label])],
                className: "w-32",
              },
              {
                label: "Shown on",
                value: query.appId,
                onChange: (appId) => setQuery({ appId }),
                options: [["", "All apps"], ...appList.map((a) => [a.appId, a.appName || a.appId])],
                className: "w-48",
              },
            ]}
            onClear={() => setQuery({ q: "", state: "", tone: "", appId: "" })}
          />
        )}
        {loading ? (
          <div className="space-y-3 p-5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-16 animate-pulse rounded-xl bg-ink-100" />
            ))}
          </div>
        ) : list.length === 0 && filtered ? (
          <div className="px-6 py-14 text-center">
            <h3 className="text-[14px] font-semibold text-ink-900">No announcements match</h3>
            <p className="mt-1 text-[13px] text-ink-500">Try another search, state, type or app.</p>
          </div>
        ) : list.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 to-violet-600 text-white shadow-brand-glow">
              <Megaphone size={24} />
            </span>
            <h3 className="mt-5 text-[15px] font-semibold text-ink-900">No announcements yet</h3>
            <p className="mx-auto mt-1.5 max-w-sm text-[13px] leading-relaxed text-ink-500">
              Let customers know about maintenance, new features or anything important — it appears at the top of their app page.
            </p>
            {!editingId && (
              <Button variant="secondary" size="sm" className="mt-5" onClick={startNew} icon={<Plus size={14} />}>
                Write the first one
              </Button>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-ink-150">
            {list.map((a) => {
              const t = TONES[a.tone] || TONES.info;
              const Icon = t.icon;
              const st = stateOf(a);
              return (
                <li key={a._id} className={cx("flex items-start gap-4 px-5 py-4 transition-colors", editingId === a._id ? "bg-brand-50/50" : "hover:bg-ink-50/60")}>
                  <span className={cx("mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl", t.iconWrap)}>
                    <Icon size={16} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate text-[14px] font-semibold text-ink-900">{a.title}</p>
                      <span className={cx("inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1", st.cls)}>
                        <span className={cx("h-1.5 w-1.5 rounded-full", st.dot)} />
                        {st.label}
                      </span>
                    </div>
                    {a.message && <p className="mt-0.5 line-clamp-1 text-[13px] text-ink-500">{a.message}</p>}
                    <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-ink-400">
                      <span>{a.appIds?.length ? a.appIds.map((id) => appName[id] || id).join(", ") : "Every app"}</span>
                      {(a.startsAt || a.endsAt) && (
                        <span className="inline-flex items-center gap-1">
                          <CalendarClock size={11} />
                          {a.startsAt ? formatDate(a.startsAt) : "Now"} → {a.endsAt ? formatDate(a.endsAt) : "no end"}
                        </span>
                      )}
                      {!a.dismissible && <span>Can't be dismissed</span>}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button type="button" onClick={() => toggle(a)} title={a.active ? "Pause" : "Resume"} aria-label={a.active ? "Pause" : "Resume"} className="grid h-8 w-8 place-items-center rounded-lg text-ink-400 hover:bg-ink-100 hover:text-ink-700">
                      {a.active ? <Pause size={15} /> : <Play size={15} />}
                    </button>
                    <button type="button" onClick={() => startEdit(a)} title="Edit" aria-label="Edit" className="grid h-8 w-8 place-items-center rounded-lg text-ink-400 hover:bg-ink-100 hover:text-ink-700">
                      <Pencil size={15} />
                    </button>
                    <button type="button" onClick={() => remove(a)} title="Delete" aria-label="Delete" className="grid h-8 w-8 place-items-center rounded-lg text-ink-400 hover:bg-rose-50 hover:text-rose-600">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {!loading && (
          <Pagination
            page={data.page}
            pages={data.pages}
            total={data.total}
            limit={data.limit}
            noun={data.total === 1 ? "announcement" : "announcements"}
            onPage={(page) => setQuery({ page })}
            onLimit={(limit) => setQuery({ limit })}
          />
        )}
      </div>
    </div>
  );
};

export default Announcements;
